(() => {
  'use strict';

  const VERIFIED_EMAIL = 'info@floral-masters.com';
  const REVIEW_URL = 'https://g.page/r/CcykrrOGSfpfEBM/review';
  const FIX_VERSION = '2026-09-08.2';

  const selectAll = (root, selector) => {
    const matchesRoot =
      root instanceof Element && root.matches(selector) ? [root] : [];
    return [...matchesRoot, ...root.querySelectorAll(selector)];
  };

  const replaceVisibleText = (root, pattern, replacement) => {
    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (
            parent.closest(
              'script,style,noscript,template,textarea,[data-no-fm-fix]'
            )
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          pattern.lastIndex = 0;
          const matches = pattern.test(node.nodeValue || '');
          pattern.lastIndex = 0;
          return matches
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      node.nodeValue = (node.nodeValue || '').replace(pattern, replacement);
    });
  };

  const normalizeEmails = (root) => {
    selectAll(root, 'a[href^="mailto:"]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      const lower = href.toLowerCase();

      if (
        !lower.startsWith('mailto:floralmasters.usa@gmail.com')
      ) {
        return;
      }

      const queryIndex = href.indexOf('?');
      const query = queryIndex >= 0 ? href.slice(queryIndex) : '';
      link.setAttribute('href', `mailto:${VERIFIED_EMAIL}${query}`);
    });

    replaceVisibleText(
      root,
      /(?:floralmasters\.usa@gmail\.com)/gi,
      VERIFIED_EMAIL
    );
  };

  const normalizeReviewLinks = (root) => {
    selectAll(root, 'a[href]').forEach((link) => {
      const href = (link.getAttribute('href') || '').toLowerCase();
      const label = (link.textContent || '').toLowerCase();
      const looksLikeReviewLink =
        label.includes('leave a review') ||
        label.includes('write a review') ||
        label.includes('review us on google');
      const pointsToGoogle =
        href.includes('google.com/') ||
        href.includes('maps.google.') ||
        href.includes('g.page/');

      if (!looksLikeReviewLink || !pointsToGoogle) return;

      link.setAttribute('href', REVIEW_URL);
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
  };

  const improveContactFields = (root) => {
    const fields = [
      ['input[name="contact[name]"]', 'Your name', 'name'],
      ['input[name="contact[email]"]', 'Email address', 'email'],
      ['input[name="contact[phone]"]', 'Phone number', 'tel'],
      ['textarea[name="contact[body]"]', 'How can we help?', null]
    ];

    fields.forEach(([selector, label, autocomplete]) => {
      selectAll(root, selector).forEach((field) => {
        if (!field.getAttribute('aria-label')) {
          field.setAttribute('aria-label', label);
        }
        if (autocomplete && !field.getAttribute('autocomplete')) {
          field.setAttribute('autocomplete', autocomplete);
        }
      });
    });
  };


  const parseTechnicalSearch = (value) => {
    const match = String(value || '').trim().match(/^title:"([^"]+)"\s+OR\s+tag:"([^"]+)"$/i);
    return match ? match[1].trim() : String(value || '').trim();
  };

  const improveSearchRelevance = (root) => {
    selectAll(root, 'form[action*="/search"]').forEach((form) => {
      const input = form.querySelector('input[name="q"]');
      if (!input) return;

      const cleanValue = parseTechnicalSearch(input.value);
      if (cleanValue && cleanValue !== input.value) input.value = cleanValue;

      if (form.dataset.fmSearchRelevance === 'true') return;
      form.dataset.fmSearchRelevance = 'true';
      form.addEventListener('submit', () => {
        const raw = parseTechnicalSearch(input.value);
        const safe = raw.replace(/["\\]/g, ' ').replace(/\s+/g, ' ').trim();
        if (!safe) return;
        input.value = `title:"${safe}" OR tag:"${safe}"`;
      });
    });

    selectAll(root, '[data-fm-search-summary], p[aria-live="polite"]').forEach((summary) => {
      summary.childNodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        node.textContent = node.textContent.replace(
          /title:"([^"]+)"\s+OR\s+tag:"[^"]+"/gi,
          '$1'
        );
      });
    });
  };

  const secureExternalLinks = (root) => {
    selectAll(root, 'a[target="_blank"]').forEach((link) => {
      const rel = new Set(
        (link.getAttribute('rel') || '').split(/\s+/).filter(Boolean)
      );
      rel.add('noopener');
      rel.add('noreferrer');
      link.setAttribute('rel', [...rel].join(' '));
    });
  };

  const improveBalloonNumberFields = (root) => {
    selectAll(root, '[data-fm-number-request]').forEach((wrapper) => {
      if (wrapper.dataset.fmNumberReady === 'true') return;

      const input = wrapper.querySelector('[data-fm-exact-number]');
      const error = wrapper.querySelector('[data-fm-number-error]');
      const formId = input?.getAttribute('form');
      const form = formId ? document.getElementById(formId) : input?.closest('form');
      if (!input || !form) return;

      wrapper.dataset.fmNumberReady = 'true';

      const selectedVariantTitle = () => {
        const radio = form.querySelector('[name="id"]:checked');
        if (radio) return radio.dataset.sizeLabel || '';
        const select = form.querySelector('select[name="id"]');
        return select?.selectedOptions?.[0]?.dataset?.sizeLabel || '';
      };

      const validate = (showMessage = false) => {
        const raw = String(input.value || '').trim();
        let message = '';

        if (!/^\d{1,2}$/.test(raw)) {
          message = wrapper.dataset.fmNumberMode === 'age'
            ? 'Enter the exact age as a whole number from 0 to 99.'
            : 'Enter the exact number as a whole number from 0 to 99.';
        } else {
          const value = Number(raw);
          const variantTitle = selectedVariantTitle();

          if (wrapper.dataset.fmNumberMode === 'digit-count') {
            if (/One Digit/i.test(variantTitle) && value > 9) {
              message = 'Choose a One Digit number from 0 to 9, or switch to Two Digits.';
            } else if (/Two Digits/i.test(variantTitle) && value < 10) {
              message = 'Choose a Two Digits number from 10 to 99, or switch to One Digit.';
            }
          }
        }

        input.setCustomValidity(message);
        if (error) {
          error.textContent = message;
          error.hidden = !message || !showMessage;
        }
        wrapper.classList.toggle('has-error', Boolean(message && showMessage));
        return !message;
      };

      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 2);
        validate(input.value.length > 0);
      });

      form.addEventListener('change', (event) => {
        if (event.target?.name === 'id') validate(input.value.length > 0);
      });

      form.addEventListener('submit', (event) => {
        if (validate(true)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        input.focus();
        input.reportValidity();
      }, true);

      validate(false);
    });
  };



  const improveEmbeddedFrames = (root) => {
    selectAll(root, 'iframe:not([title])').forEach((frame) => {
      const id = String(frame.id || frame.name || '').toLowerCase();
      const title = id.includes('merchantwidget')
        ? 'Google merchant information'
        : id.includes('web-pixel')
          ? 'Analytics service'
          : 'Embedded content';
      frame.setAttribute('title', title);
    });

    selectAll(root, '.fm-google-review__g').forEach((letter) => {
      letter.setAttribute('aria-hidden', 'true');
    });
  };

  const applyFixes = (root = document) => {
    normalizeEmails(root);
    normalizeReviewLinks(root);
    improveContactFields(root);
    improveSearchRelevance(root);
    secureExternalLinks(root);
    improveBalloonNumberFields(root);
    improveEmbeddedFrames(root);
    document.documentElement.dataset.fmFixVersion = FIX_VERSION;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyFixes());
  } else {
    applyFixes();
  }


  const fmFrameObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) improveEmbeddedFrames(node);
      });
    });
  });
  if (document.documentElement) {
    fmFrameObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    applyFixes(event.target || document);
  });
})();
